import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import InsightsPanel from './InsightsPanel';
import { errorText, formatDate, formatDuration, formatKg, formatNumber, parseLocalDay } from './strengthFormat';
import './ProgressView.css';

function metricFor(kind) {
  if (kind === 'Timed') return { field: 'bestDurationSec', labelKey: 'strength.progress.bestHold', format: (v) => formatDuration(v) };
  if (kind === 'Bodyweight') return { field: 'bestReps', labelKey: 'strength.progress.bestReps', format: (v) => `${v} reps` };
  return { field: 'estimatedMaxKg', labelKey: 'strength.progress.estimatedMax', format: null };
}

function ProgressView({ api, t, lang, members, showMessage }) {
  const [userId, setUserId] = useState('');
  const [weeks, setWeeks] = useState(12);
  const [myId, setMyId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [exerciseKey, setExerciseKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setProgress(null);
    api.progress(userId || undefined, weeks)
      .then(data => {
        if (cancelled) return;
        if (!userId) setMyId(data?.userId || null);
        setProgress(data);
        // Open on the most-trained lift: its estimated max is the most telling line.
        const exercises = data?.exercises || [];
        const lifts = exercises.filter(e => e.kind === 'Weighted');
        const pick = (lifts.length ? lifts : exercises).reduce((best, e) => (!best || e.sessionCount > best.sessionCount ? e : best), null);
        setExerciseKey(pick?.exerciseKey || null);
      })
      .catch(e => {
        if (!cancelled) {
          setProgress({ weeks: [], exercises: [], totalSessions: 0, sessionsThisWeek: 0, weekStreak: 0 });
          showMessage(errorText(e, t('strength.errors.load')), 'error');
        }
      });
    return () => { cancelled = true; };
  }, [api, userId, weeks, showMessage, t]);

  const weekData = useMemo(() => (progress?.weeks || []).map(w => ({
    label: formatDate(parseLocalDay(w.weekStart), lang, { day: 'numeric', month: 'short' }),
    volume: Math.round(w.volumeKg),
    sessions: w.sessions
  })), [progress, lang]);

  const exercise = progress?.exercises?.find(e => e.exerciseKey === exerciseKey);
  const metric = metricFor(exercise?.kind);
  const formatMetric = metric.format || ((v) => formatKg(v, lang));
  const exerciseData = useMemo(() => (exercise?.points || []).map(p => ({
    label: formatDate(parseLocalDay(p.date), lang, { day: 'numeric', month: 'short' }),
    value: p[metric.field],
    top: p.topWeightKg
  })), [exercise, metric.field, lang]);

  return (
    <div className="st-progress">
      {members.length > 1 && (
        <div className="st-chips" role="group" aria-label={t('strength.history.whose')}>
          <button type="button" className={`st-chip ${userId === '' ? 'active' : ''}`} onClick={() => setUserId('')}>
            {t('strength.common.me')}
          </button>
          {members.filter(m => m.userId !== myId).map(m => (
            <button type="button" key={m.userId} className={`st-chip ${userId === m.userId ? 'active' : ''}`} onClick={() => setUserId(m.userId)}>
              {m.name}
            </button>
          ))}
        </div>
      )}

      <InsightsPanel api={api} t={t} lang={lang} userId={userId || undefined} showMessage={showMessage} />

      {!progress ? (
        <div className="st-empty">{t('strength.loading')}</div>
      ) : progress.totalSessions === 0 ? (
        <div className="st-empty">{t('strength.progress.empty')}</div>
      ) : (
        <>
          <dl className="st-stats st-progress-stats">
            <div><dt>{t('strength.progress.thisWeek')}</dt><dd>{progress.sessionsThisWeek}</dd></div>
            <div><dt>{t('strength.progress.streak')}</dt><dd>{t('strength.progress.weeks', { count: progress.weekStreak })}</dd></div>
            <div><dt>{t('strength.progress.total')}</dt><dd>{progress.totalSessions}</dd></div>
          </dl>

          <section className="st-card">
            <div className="st-row st-row-between">
              <h3>{t('strength.progress.weeklyVolume')}</h3>
              <select className="st-select" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} aria-label={t('strength.progress.range')}>
                {[12, 26, 52].map(n => <option key={n} value={n}>{t('strength.progress.weeks', { count: n })}</option>)}
              </select>
            </div>
            <div className="st-chart">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={weekData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={48} tickFormatter={(v) => formatNumber(v, lang, 0)} />
                  <Tooltip
                    formatter={(value, name, item) => [
                      `${formatKg(value, lang)} · ${t('strength.progress.sessions', { count: item.payload.sessions })}`,
                      t('strength.summary.volume')
                    ]}
                  />
                  <Bar dataKey="volume" fill="var(--primary-500)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="st-card">
            <div className="st-row st-row-between">
              <h3>{t('strength.progress.perExercise')}</h3>
              <select className="st-select" value={exerciseKey || ''} onChange={(e) => setExerciseKey(e.target.value)} aria-label={t('strength.progress.chooseExercise')}>
                {progress.exercises.map(e => <option key={e.exerciseKey} value={e.exerciseKey}>{e.exerciseName}</option>)}
              </select>
            </div>
            {exercise && (
              <>
                <p className="st-muted st-small">{t(metric.labelKey)}</p>
                <div className="st-chart">
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={exerciseData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-border)" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={48} domain={['auto', 'auto']} />
                      <Tooltip formatter={(value, name) => [formatMetric(value), name]} />
                      {exercise.kind === 'Weighted' && <Legend wrapperStyle={{ fontSize: 12 }} />}
                      <Line type="monotone" dataKey="value" name={t(metric.labelKey)} stroke="var(--accent-600)" strokeWidth={2} dot={{ r: 3 }} />
                      {exercise.kind === 'Weighted' && (
                        <Line type="monotone" dataKey="top" name={t('strength.progress.topWeight')} stroke="var(--primary-500)" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </section>

          <section className="st-card">
            <h3>{t('strength.progress.bests')}</h3>
            <table className="st-table">
              <thead>
                <tr>
                  <th>{t('strength.progress.exercise')}</th>
                  <th>{t('strength.progress.best')}</th>
                  <th>{t('strength.progress.sessionsCol')}</th>
                  <th>{t('strength.progress.last')}</th>
                </tr>
              </thead>
              <tbody>
                {progress.exercises.map(e => (
                  <tr key={e.exerciseKey}>
                    <td>{e.exerciseName}</td>
                    <td>
                      {e.kind === 'Timed' ? formatDuration(e.bestDurationSec)
                        : e.kind === 'Bodyweight' && !e.bestWeightKg ? `${e.bestReps} reps`
                          : `${formatKg(e.bestWeightKg, lang)} (e1RM ${formatKg(e.bestEstimatedMaxKg, lang)})`}
                    </td>
                    <td>{e.sessionCount}</td>
                    <td>{formatDate(parseLocalDay(e.lastPerformedAt), lang, { day: 'numeric', month: 'short' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

export default ProgressView;
